/**
 * Cartao do WEBHOOK GENERICO: o contrato canonico do Converte Facil, para
 * checkout proprio e qualquer plataforma sem adaptador dedicado.
 *
 * Reusa `CartaoProvedor` (URL, segredo, teste, historico) e acrescenta o que
 * so o generico tem: o cabecalho da assinatura, um exemplo de corpo montado
 * com uma oferta da PROPRIA empresa (a pessoa reconhece o produto e entende o
 * campo) e o contrato completo num modal.
 *
 * Decisoes nao obvias:
 *  - O contrato mora no modal, e nao num link externo: o documento
 *    (docs/INTEGRACOES-WEBHOOK-GENERICO.md) esta no repositorio privado e a
 *    previa nao o serve. O modal e a versao que o integrador da empresa le na
 *    hora; o caminho do documento aparece no rodape para quem tem o repositorio.
 *  - O exemplo e copiavel em bloco (multilinha), diferente do `CampoCopiavel`
 *    de uma linha. Sem clipboard (http sem TLS), o bloco fica selecionado para
 *    Ctrl+C, e nada finge que copiou.
 *  - A lista de plataformas que "entram por aqui" vem do plano de integracoes
 *    (docs/PLANO-INTEGRACOES-BR.md): sao nomes de plataformas, nao produtos de
 *    clientes.
 */

import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Footnote, Modal } from '../../../components/ui';
import { ofertaCiclica } from '../../../lib/mock/catalogo';
import type { CompanySummary } from '../../../types';
import type { WebhookIntegration } from '../types';
import { CONTRATO_GENERICO, EVENT_LABEL, PLATAFORMAS_PELO_GENERICO, exemploDeCorpoGenerico } from '../vocabulario';
import { CampoCopiavel } from './CampoCopiavel';
import { CartaoProvedor } from './CartaoProvedor';

const FEEDBACK_MS = 2_400;
const PLATAFORMAS_VISIVEIS = 8;

type EstadoDaCopia = 'parado' | 'copiado' | 'manual';

function BlocoCopiavel({ label, texto }: { readonly label: string; readonly texto: string }): JSX.Element {
  const [estado, setEstado] = useState<EstadoDaCopia>('parado');
  const preRef = useRef<HTMLPreElement | null>(null);

  useEffect(() => {
    if (estado === 'parado') {
      return undefined;
    }
    const timer = window.setTimeout(() => setEstado('parado'), FEEDBACK_MS);
    return () => window.clearTimeout(timer);
  }, [estado]);

  const copiar = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(texto);
      setEstado('copiado');
    } catch {
      const pre = preRef.current;
      const selecao = window.getSelection();
      if (pre !== null && selecao !== null) {
        const range = document.createRange();
        range.selectNodeContents(pre);
        selecao.removeAllRanges();
        selecao.addRange(range);
      }
      setEstado('manual');
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink">{label}</span>
        <span className="flex items-center gap-2">
          <span role="status">
            {estado === 'copiado' ? (
              <Badge tone="good" icon="check">
                Copiado
              </Badge>
            ) : null}
            {estado === 'manual' ? (
              <Badge tone="warning" dot>
                Selecionado: use Ctrl+C
              </Badge>
            ) : null}
          </span>
          <Button variant="secondary" size="sm" icon="copiar" onClick={() => void copiar()}>
            Copiar
          </Button>
        </span>
      </div>
      <pre ref={preRef} tabIndex={0} className="max-h-64 overflow-auto rounded-lg border border-line bg-elevated px-3 py-2 font-mono text-[11px] leading-relaxed text-ink">
        <code>{texto}</code>
      </pre>
    </div>
  );
}

function Tabela({ colunas, linhas }: { readonly colunas: readonly string[]; readonly linhas: readonly (readonly string[])[] }): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-line bg-elevated text-left text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
            {colunas.map((c) => (
              <th key={c} scope="col" className="px-3 py-2">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {linhas.map((linha) => (
            <tr key={linha.join('|')}>
              {linha.map((celula, i) => (
                <td key={`${i}-${celula}`} className={`px-3 py-2 align-top ${i === 0 ? 'font-mono text-[11px] text-ink' : 'text-ink-muted'}`}>
                  {celula}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CartaoGenerico({ integracao, company }: { readonly integracao: WebhookIntegration; readonly company: CompanySummary }): JSX.Element {
  const [contratoAberto, setContratoAberto] = useState(false);
  const exemplo = exemploDeCorpoGenerico(ofertaCiclica(company.id, 0), company.slug);
  const visiveis = PLATAFORMAS_PELO_GENERICO.slice(0, PLATAFORMAS_VISIVEIS);
  const restantes = PLATAFORMAS_PELO_GENERICO.length - visiveis.length;

  return (
    <CartaoProvedor
      integracao={integracao}
      extra={
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <CampoCopiavel
              label="Cabeçalho da assinatura"
              value={CONTRATO_GENERICO.header}
              hint="HMAC-SHA256 em hexadecimal do corpo cru, com o segredo da integração. Também aceitamos x-signature e o prefixo sha256=."
            />
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">Quem entra por aqui</span>
              <p className="flex flex-wrap items-center gap-1.5">
                {visiveis.map((nome) => (
                  <Badge key={nome} tone="neutral">
                    {nome}
                  </Badge>
                ))}
                {restantes > 0 ? <Badge tone="neutral">+{restantes}</Badge> : null}
              </p>
              <p className="text-xs text-ink-subtle">
                Checkout próprio, gateways, ERPs e automações. Plataformas sem adaptador dedicado usam este contrato até ganharem o próprio, por ondas (plano de integrações).
              </p>
            </div>
          </div>
          <BlocoCopiavel label="Exemplo de corpo (com uma oferta desta empresa)" texto={exemplo} />
          <div>
            <Button variant="secondary" icon="modelo" onClick={() => setContratoAberto(true)}>
              Ver o contrato completo
            </Button>
          </div>

          <Modal
            open={contratoAberto}
            onClose={() => setContratoAberto(false)}
            size="lg"
            title="Contrato do webhook genérico"
            description="Qualquer sistema que faça um POST com JSON alimenta o motor por este contrato. Os adaptadores dedicados só traduzem o formato de cada plataforma para ele."
            footer={
              <Button variant="primary" onClick={() => setContratoAberto(false)}>
                Entendi
              </Button>
            }
          >
            <div className="flex flex-col gap-5 text-sm">
              <section className="flex flex-col gap-2">
                <h3 className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Endereço</h3>
                <pre className="overflow-x-auto rounded-lg border border-line bg-elevated px-3 py-2 font-mono text-[11px] leading-relaxed text-ink">
                  <code>{`POST ${integracao.webhookUrl}\nContent-Type: application/json\n${CONTRATO_GENERICO.header}: <HMAC-SHA256 em hex do corpo cru, com o segredo da integração>`}</code>
                </pre>
                <p className="text-xs text-ink-muted">
                  A assinatura é calculada sobre os bytes exatos do corpo enviado: não reserialize o JSON depois de assinar. O segredo é configurado pela administração e nunca aparece
                  neste painel.
                </p>
              </section>

              <section className="flex flex-col gap-2">
                <h3 className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Campos</h3>
                <Tabela colunas={['Campo', 'Obrigatório', 'Regra']} linhas={CONTRATO_GENERICO.campos.map((c) => [c.campo, c.obrigatorio, c.regra])} />
              </section>

              <section className="flex flex-col gap-2">
                <h3 className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Eventos</h3>
                <Tabela
                  colunas={['event', 'Sinônimos aceitos', 'O que o motor faz']}
                  linhas={CONTRATO_GENERICO.eventos.map((e) => [`${e.tipo} · ${EVENT_LABEL[e.tipo]}`, e.sinonimos, e.efeito])}
                />
                <p className="text-xs text-ink-muted">
                  Todo evento parte de um fato já ocorrido: o carrinho entra na fila de 30 minutos e a assinatura vencida entra no funil de expirados. Não existe evento de
                  “vai vencer”.
                </p>
              </section>

              <section className="flex flex-col gap-2">
                <h3 className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Respostas</h3>
                <Tabela colunas={['Código', 'Significado']} linhas={CONTRATO_GENERICO.respostas.map((r) => [String(r.codigo), r.significado])} />
              </section>

              <section className="flex flex-col gap-2">
                <h3 className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Teste rápido (bash)</h3>
                <pre className="overflow-x-auto rounded-lg border border-line bg-elevated px-3 py-2 font-mono text-[11px] leading-relaxed text-ink">
                  <code>{CONTRATO_GENERICO.testeRapido(integracao.webhookUrl)}</code>
                </pre>
                <p className="text-xs text-ink-muted">Em modo simulado do motor nenhuma ligação ou mensagem sai: use para validar a integração antes de ligar os motores.</p>
              </section>

              <Footnote>
                Documento completo no repositório: {CONTRATO_GENERICO.docs}. Plano de adaptadores dedicados por onda: {CONTRATO_GENERICO.plano}.
              </Footnote>
            </div>
          </Modal>
        </div>
      }
    />
  );
}
